import { useState, useCallback } from "react";
import {
  getConversationHistory,
  getApiConversationId,
  ConversationHistoryResponse,
  ConversationEvent,
} from "../api/conversation/conversation";
import { v4 as uuidv4 } from "uuid";
import { Message, Conversation } from "@/types";
import { mapEventsToChevronMessages } from "./conversationChevronMapper";
import { useFeatureFlags } from "@/utils/FeatureFlagDetection";

const USER_ROLE = "user";
const KNOWN_ROLES = new Set(["user", "assistant"]);

/**
 * AgentCore stores content.text as a JSON envelope:
 *   {"message": {"role": "...", "content": [{"text": "..."}]}, ...}
 *
 * This extracts the display text and role from the envelope.
 * Returns null if the content is a tool message that should be hidden.
 * Returns undefined if the content isn't an envelope (plain text).
 */
function tryUnwrapEnvelope(
  raw: string,
): { text: string; isUser: boolean } | null | undefined {
  const trimmed = raw.trim();
  if (!trimmed.startsWith("{")) return undefined;

  try {
    const parsed = JSON.parse(trimmed);

    // Direct toolResult/toolUse — hide
    if (parsed.toolResult || parsed.toolUse) return null;

    // Standard envelope: {"message": {"role": "user"|"assistant", "content": [...]}}
    // Require a known role to avoid false positives on arbitrary JSON with message.content
    const role =
      typeof parsed.message?.role === "string"
        ? parsed.message.role.trim().toLowerCase()
        : undefined;

    if (
      !parsed?.message?.content ||
      !Array.isArray(parsed.message.content) ||
      !role ||
      !KNOWN_ROLES.has(role)
    ) {
      return undefined;
    }

    const items = parsed.message.content as Record<string, unknown>[];

    // Extract text parts (tool items like toolResult/toolUse are ignored)
    const text = items
      .filter((item) => typeof item.text === "string")
      .map((item) => item.text as string)
      .join("\n");

    if (!text) return null;

    return { text, isUser: role === USER_ROLE };
  } catch {
    return undefined;
  }
}

/** Maps a ConversationEvent to a Message. Returns null to skip the event. */
function mapEventToMessage(event: ConversationEvent): Message | null {
  const conversational = (event.payload || []).find(
    (entry) => entry.conversational,
  )?.conversational as
    | { content?: { text?: string }; role?: string }
    | undefined;

  const rawContent = conversational?.content?.text || "";
  const rawRole = conversational?.role?.trim().toLowerCase();

  // Try to unwrap JSON envelope; null = tool message (skip), undefined = not an envelope
  const unwrapped = rawContent ? tryUnwrapEnvelope(rawContent) : undefined;

  if (unwrapped === null) return null;

  const content = unwrapped?.text ?? rawContent;
  const isUser = unwrapped?.isUser ?? rawRole === USER_ROLE;

  if (!content.trim()) return null;

  return {
    id: event.eventId || `event_${uuidv4()}`,
    content,
    isUser,
  };
}

/** Maps API conversation history events into a Conversation object. */
function mapEventsToConversation(
  projectId: string,
  events: ConversationEvent[],
  nextToken?: string,
  /** When true, tool calls are preserved as `chatItems` for inline chevrons. */
  includeToolChevrons = false,
): { conversation: Conversation; nextToken?: string } {
  // Reverse from newest-first to chronological order
  const chronologicalEvents = [...events].reverse();
  const messages = includeToolChevrons
    ? mapEventsToChevronMessages(chronologicalEvents)
    : chronologicalEvents
        .map(mapEventToMessage)
        .filter((msg): msg is Message => msg !== null);

  const firstUserMessage = messages.find((m) => m.isUser);
  const title = firstUserMessage
    ? firstUserMessage.content.slice(0, 50) +
      (firstUserMessage.content.length > 50 ? "..." : "")
    : undefined;

  const firstEventTimestamp = chronologicalEvents[0]?.eventTimestamp;
  const createdAt = firstEventTimestamp
    ? new Date(firstEventTimestamp)
    : new Date();

  return {
    conversation: {
      id: getApiConversationId(projectId),
      title,
      messages,
      createdAt,
      projectId,
    },
    nextToken,
  };
}

/**
 * Custom hook for fetching conversation history from the API
 *
 * Calls the GET /projects/{projectId}/conversation endpoint and maps
 * Bedrock AgentCore events to the app's Message/Conversation types.
 *
 * @example
 * ```typescript
 * const { isLoading, error, fetchConversationHistory } = useConversationHistory();
 *
 * // Load conversation history for a project
 * const result = await fetchConversationHistory("project-123");
 * if (result) {
 *   console.log(result.conversation.messages);
 *   // Load more with pagination
 *   if (result.nextToken) {
 *     const more = await fetchConversationHistory("project-123", result.nextToken);
 *   }
 * }
 * ```
 */
export const useConversationHistory = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { isThinkingChevronEnabled } = useFeatureFlags(["thinkingChevron"]);

  const fetchConversationHistory = useCallback(
    async (projectId: string, nextToken?: string) => {
      try {
        setIsLoading(true);
        setError(null);

        const response: ConversationHistoryResponse =
          await getConversationHistory({ projectId, nextToken });

        if (response.success && response.data) {
          return mapEventsToConversation(
            projectId,
            response.data.events,
            response.data.nextToken,
            isThinkingChevronEnabled,
          );
        } else {
          setError("Failed to fetch conversation history");
          return null;
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : "Failed to fetch conversation history";
        setError(errorMessage);
        console.error("Error fetching conversation history:", err);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [isThinkingChevronEnabled],
  );

  return { isLoading, error, fetchConversationHistory };
};
